import { useEffect, useRef, type ReactElement } from 'react';
import {
  FaCheck,
  FaEllipsisH,
  FaExclamationTriangle,
  FaQuestion,
  FaServer,
  FaTimes,
  FaWifi,
} from 'react-icons/fa';
import type { AccessRoute } from '../../utils/pwa';
import type { ConnectionProbe } from '../../hooks/useServerReachability';
import { buildConnectionGuidance, type DiagnosticState } from './connectionGuidance';
import styles from './ConnectionLostOverlay.module.css';

/**
 * 内訳 1 行の記号。色だけに意味を持たせない（色覚・モノクロ環境でも読める）。
 * 「未確認」に − を使わないのは、臨床では ✓ / ✗ / − が陰性・陽性の記号として
 * 読まれてしまい、「判定していない」の意味にならないため。
 */
const DIAGNOSTIC_MARK: Record<DiagnosticState, ReactElement> = {
  ok: <FaCheck />,
  ng: <FaTimes />,
  slow: <FaExclamationTriangle />,
  unchecked: <FaEllipsisH />,
  unknown: <FaQuestion />,
};

export interface ConnectionLostOverlayProps {
  /** 到達性プローブの結果（status と段ごとの実測） */
  probe: ConnectionProbe;
  /** プローブ実行中（［再試行］を押した直後など） */
  isChecking: boolean;
  /** 接続経路。案内文の出し分けに使う */
  route: AccessRoute;
  /** ホーム画面から standalone 起動しているか */
  isPwa: boolean;
  /** ［再試行］ */
  onRetry: () => void;
}

/**
 * サーバに接続できないことを伝えるオーバーレイ（表示専用）。
 *
 * PWA から起動して Tailscale が落ちている場合、Service Worker がアプリシェルを
 * 返して React だけは起動する。そのとき何も案内が無いと「アプリが壊れた」ように
 * 見えるため、この画面で原因と復旧手順を示す。
 *
 * 文言の出し分けは buildConnectionGuidance に寄せてある（純粋関数・テスト容易）。
 */
const ConnectionLostOverlay = ({
  probe,
  isChecking,
  route,
  isPwa,
  onRetry,
}: ConnectionLostOverlayProps) => {
  const guidance = buildConnectionGuidance(probe, route, isPwa);
  const shouldShow = guidance !== null;
  const retryRef = useRef<HTMLButtonElement | null>(null);

  // フルスクリーン再生中は動画要素が top layer に入り、通常の DOM は z-index を
  // いくつにしても描画されない。「院外から Tailscale 経由で視聴中に切断される」は
  // 本機能の主要ユースケースそのものなので、案内を出す前にフルスクリーンを解除する。
  // どのみち配信が止まっているので、視聴継続を妨げることにはならない。
  useEffect(() => {
    if (!shouldShow) return;
    if (!document.fullscreenElement) return;
    const exitFullscreen = document.exitFullscreen?.bind(document);
    if (exitFullscreen) void exitFullscreen().catch(() => undefined);
  }, [shouldShow]);

  // 画面を覆う案内なのに、フォーカスは背後の（見えない）要素に残ったままになる。
  // 表示時に［再試行］へ移して、キーボードと支援技術の起点をこの画面に置く。
  useEffect(() => {
    if (!shouldShow) return;
    retryRef.current?.focus();
  }, [shouldShow]);

  if (!guidance) return null;

  return (
    <div
      className={styles['connection-lost-overlay']}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="connection-lost-title"
      // タイトル以外は読み上げられないことがあるので、状況と内訳を明示的に紐づける
      aria-describedby="connection-lost-lead connection-lost-diagnostics"
      data-testid="connection-lost-overlay"
    >
      <div className={styles['connection-lost-card']}>
        <span
          className={`${styles['connection-lost-badge']} ${
            styles[`connection-lost-badge--${guidance.icon}`]
          }`}
          aria-hidden="true"
        >
          {/* title は装飾アイコンの識別子（親が aria-hidden なので読み上げられない）。
              取り違えると「回線を疑わせない」という狙いが崩れるため、テストで固定する */}
          {guidance.icon === 'server' ? (
            <FaServer title="サーバー" />
          ) : (
            <FaWifi title="ネットワーク" />
          )}
        </span>

        <h2 className={styles['connection-lost-title']} id="connection-lost-title">
          {guidance.title}
        </h2>
        <p className={styles['connection-lost-lead']} id="connection-lost-lead">
          {guidance.lead}
        </p>

        {/* 「どこまで届いているのか」を最初に見せる。文章だけだと成否が読み取りづらい */}
        <p className={styles['connection-lost-diagnostics-heading']} id="connection-lost-diagnostics-heading">
          接続の確認結果
        </p>
        <ul
          className={styles['connection-lost-diagnostics']}
          id="connection-lost-diagnostics"
          aria-labelledby="connection-lost-diagnostics-heading"
          data-testid="connection-lost-diagnostics"
        >
          {guidance.diagnostics.map((diagnostic) => (
            <li
              key={diagnostic.label}
              className={`${styles['connection-lost-diagnostic']} ${
                styles[`connection-lost-diagnostic--${diagnostic.state}`]
              }`}
            >
              <span className={styles['connection-lost-diagnostic-mark']} aria-hidden="true">
                {DIAGNOSTIC_MARK[diagnostic.state]}
              </span>
              <span className={styles['connection-lost-diagnostic-label']}>{diagnostic.label}</span>
              {/* 読み上げで「…届いているか届いています」と繋がらないよう区切りを挟む */}
              <span className={styles['connection-lost-visually-hidden']}>：</span>
              <span className={styles['connection-lost-diagnostic-detail']}>
                {diagnostic.detail}
              </span>
            </li>
          ))}
        </ul>

        <ol className={styles['connection-lost-steps']}>
          {guidance.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>

        <p className={styles['connection-lost-note']}>{guidance.note}</p>

        {/*
          disabled にすると、自動再プローブのたび（15〜120 秒ごと）にフォーカスが body へ落ちる。
          この画面には他にフォーカスできる要素が無いので、戻す手段が無くなる。
          aria-disabled で「押せない」ことだけ伝え、フォーカスは保持する。
        */}
        <button
          type="button"
          ref={retryRef}
          className={styles['connection-lost-retry']}
          onClick={() => {
            if (isChecking) return;
            onRetry();
          }}
          aria-disabled={isChecking}
          data-testid="connection-lost-retry"
        >
          {isChecking ? '確認中…' : '再試行'}
        </button>
      </div>
    </div>
  );
};

export default ConnectionLostOverlay;
